import { useDebounce } from '@uidotdev/usehooks';
import type { SnapshotFrom } from 'xstate';

import { RESERVATION_ACTION_BUTTON_DEBOUNCE_MS } from '@/data/constants';

import {
  reservationFormMainMachine,
  selectEditTripLegDoneButtonDisabled,
  selectRequestOverrideButtonDisabled,
  selectSaveAsDraftButtonDisabled,
  selectTripLegNextButtonDisabled,
  type EditTripLegDoneButtonDisabledOptions,
  type TripLegNextButtonDisabledOptions,
} from '../machines/reservationFormMain.machine';

export type {
  EditTripLegDoneButtonDisabledOptions,
  TripLegNextButtonDisabledOptions,
};

export function useDebouncedReservationActionSelectors(
  mainSnapshot: SnapshotFrom<typeof reservationFormMainMachine>,
  tripLegNextOptions?: TripLegNextButtonDisabledOptions,
  editTripLegDoneOptions?: EditTripLegDoneButtonDisabledOptions,
) {
  const rawTripLegNextDisabled = selectTripLegNextButtonDisabled(
    mainSnapshot,
    tripLegNextOptions,
  );
  const rawEditTripLegDoneDisabled = selectEditTripLegDoneButtonDisabled(
    mainSnapshot,
    editTripLegDoneOptions,
  );
  const rawRequestOverrideDisabled =
    selectRequestOverrideButtonDisabled(mainSnapshot);
  const rawSaveAsDraftDisabled = selectSaveAsDraftButtonDisabled(mainSnapshot);
  const isBusy =
    mainSnapshot.matches('submitting') || mainSnapshot.matches('validating');
  const isSubmitting = mainSnapshot.matches('submitting');

  const debouncedTripLegNext = useDebounce(
    rawTripLegNextDisabled,
    RESERVATION_ACTION_BUTTON_DEBOUNCE_MS,
  );
  const debouncedEditTripLegDone = useDebounce(
    rawEditTripLegDoneDisabled,
    RESERVATION_ACTION_BUTTON_DEBOUNCE_MS,
  );
  const debouncedRequestOverride = useDebounce(
    rawRequestOverrideDisabled,
    RESERVATION_ACTION_BUTTON_DEBOUNCE_MS,
  );
  const debouncedSaveAsDraft = useDebounce(
    rawSaveAsDraftDisabled,
    RESERVATION_ACTION_BUTTON_DEBOUNCE_MS,
  );

  return {
    tripLegNextButtonDisabled: isBusy
      ? rawTripLegNextDisabled
      : debouncedTripLegNext,
    editTripLegDoneButtonDisabled: isSubmitting
      ? rawEditTripLegDoneDisabled
      : debouncedEditTripLegDone,
    requestOverrideButtonDisabled: isBusy
      ? rawRequestOverrideDisabled
      : debouncedRequestOverride,
    saveAsDraftButtonDisabled: isBusy
      ? rawSaveAsDraftDisabled
      : debouncedSaveAsDraft,
  };
}
